"use client";

/**
 * BookingUnavailable — carte affichée sur les fiches sans RDV en ligne
 * (centres, free, unclaimed, Gold/Premium sans RDV activé). Style Doctolib :
 * icône tel + message clair + CTA "Appeler le cabinet" orange. Pas de wizard
 * ni de slots fictifs.
 *
 * Praticien inscrit (compte, revendication ou Gold/Premium) : numéro affiché
 * dans le texte et tel: direct. Sinon, le clic passe par UnclaimedCallButton
 * qui ouvre la modale « ce praticien n'est pas encore inscrit sur Sahha »
 * avant de proposer le tel:.
 */
import { useTranslations } from "next-intl";
import { Num } from "@/components/ui/Num";
import { UnclaimedCallButton, isRegisteredOnSahha } from "@/components/physician/UnclaimedCallButton";

type Props = {
  phone?: string;
  /** Slug du physician — propagé au funnel tracking pour relier les events au profil. */
  physicianSlug?: string;
  /** Tier réel du médecin — propagé au metadata analytics. */
  tier?: "gold" | "premium" | "free" | "unclaimed";
  /** Placement offert 3 mois actif — propagé au tracking pour attribution. */
  trialBoosted?: "gold" | "premium";
  /** Ville — segmentation analytics. */
  city?: string;
  /** Fiche rattachée à un compte (userId) ou revendiquée (claimedAt). */
  isClaimed?: boolean;
};

export function BookingUnavailable({ phone, physicianSlug, tier, trialBoosted, city, isClaimed }: Props) {
  const t = useTranslations("booking.unavailable");
  const showPhone = Boolean(phone) && isRegisteredOnSahha({ isClaimed, tier });

  return (
    <div className="overflow-hidden rounded-3xl border border-[var(--sahha-border-default)] bg-white p-5 shadow-[0_4px_20px_-8px_rgba(15,23,42,0.08)]">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-50)]">
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="var(--sahha-primary-700)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2.07 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.79a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.89.35 1.83.59 2.79.72A2 2 0 0 1 22 16.92Z" />
          </svg>
        </span>
        <div className="flex-1">
          <h3 className="text-[16px] font-extrabold leading-tight text-[var(--sahha-text-900)]">
            {t("title")}
          </h3>
          <p className="mt-1.5 text-[13.5px] text-[var(--sahha-text-600)]">
            {phone ? t("subtitleWithPhone") : t("subtitleNoPhone")}
            {showPhone ? (
              <>
                {" "}
                <Num className="font-semibold text-[var(--sahha-text-900)]">{phone}</Num>
              </>
            ) : null}
          </p>
        </div>
      </div>

      {phone ? (
        <UnclaimedCallButton
          phone={phone}
          source="physician_page_unavailable"
          physicianSlug={physicianSlug}
          tier={tier}
          trialBoosted={trialBoosted}
          city={city}
          isClaimed={isClaimed}
          phoneVisible={showPhone}
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[var(--sahha-accent-500,#F4A261)] text-[14px] font-bold uppercase tracking-wider text-white transition hover:bg-[var(--sahha-accent-600,#E8924E)] active:scale-[0.99]"
        >
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2A19.79 19.79 0 0 1 2.07 4.18 2 2 0 0 1 4.06 2h3a2 2 0 0 1 2 1.72c.13.96.37 1.9.72 2.79a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.89.35 1.83.59 2.79.72A2 2 0 0 1 22 16.92Z" />
          </svg>
          {t("callCta")}
        </UnclaimedCallButton>
      ) : (
        <div className="mt-5 rounded-2xl border border-dashed border-[var(--sahha-border-default)] bg-[var(--sahha-surface-1)] p-4 text-center text-[12.5px] text-[var(--sahha-text-600)]">
          {t("noPhoneFallback")}
        </div>
      )}

      <p className="mt-3 text-center text-[11.5px] text-[var(--sahha-text-500)]">
        {t("hint")}
      </p>
    </div>
  );
}
